import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Button, StatusPill } from '@bb/ui';
import { ApiError, type A11ySettings, type BrandThemeView, type BrandTokens } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';
import { A11yIssuesPanel } from '../composer/shared/A11yIssuesPanel.tsx';

const COLORS: Array<[keyof BrandTokens['colors'], string]> = [
  ['primary', 'Primary'],
  ['background', 'Background'],
  ['text', 'Text'],
  ['button', 'Button'],
  ['buttonText', 'Button text'],
];
const CODES: Array<[keyof A11ySettings['severity'] & string, string]> = [
  ['missing_alt_text', 'Image without alt text'],
  ['low_contrast', 'Low text contrast'],
  ['missing_button_label', 'Button or link without a label'],
  ['small_tap_target', 'Tap target under 44 pt'],
  ['missing_accessibility_language', 'Missing accessibility language'],
];
const DEFAULT_TOKENS: BrandTokens = { colors: { primary: '#0057ff', background: '#ffffff', text: '#111111', button: '#0057ff', buttonText: '#ffffff' }, fonts: { family: 'System', titleSize: 20, bodySize: 15, buttonSize: 16 }, radii: { button: 8, container: 12 } };
/** A sample message the theme is checked against: title, body, image and button take the theme colours. */
const SAMPLE = { accessibilityLanguage: 'en', pages: [{ rows: [{ columns: [{ blocks: [{ kind: 'title', text: 'Deposit bonus' }, { kind: 'paragraph', text: 'Top up today and get 10 % extra.' }, { kind: 'image', alt: 'Bonus illustration' }, { kind: 'button', label: 'Get the bonus' }] }] }] }] };
const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

function ThemeForm({ environmentId, theme, onDone }: { environmentId: string; theme: BrandThemeView | null; onDone: () => void }) {
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/brand' });
  const queryClient = useQueryClient();
  const [name, setName] = useState(theme?.name ?? '');
  const [tokens, setTokens] = useState<BrandTokens>(theme?.tokens ?? DEFAULT_TOKENS);
  const save = useMutation({
    mutationFn: () => (theme ? api.updateBrandTheme(environmentId, theme.id, { name: name.trim(), tokens }) : api.createBrandTheme(environmentId, { name: name.trim(), tokens })),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['brand-themes', environmentId] });
      onDone();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) save.mutate();
  };
  return (
    <form className="stack-form panel" aria-label={theme ? `Edit ${theme.name}` : 'New theme'} onSubmit={submit}>
      <label>
        Theme name
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      {COLORS.map(([key, label]) => (
        <label key={key}>
          {label} colour
          <input value={tokens.colors[key]} onChange={(e) => setTokens({ ...tokens, colors: { ...tokens.colors, [key]: e.target.value } })} />
          <span className="swatch" style={{ background: tokens.colors[key] }} aria-hidden="true" />
        </label>
      ))}
      <label>
        Font family
        <input value={tokens.fonts.family} onChange={(e) => setTokens({ ...tokens, fonts: { ...tokens.fonts, family: e.target.value } })} />
      </label>
      <label>
        Button radius
        <input type="number" min={0} max={64} value={tokens.radii.button} onChange={(e) => setTokens({ ...tokens, radii: { ...tokens.radii, button: Number(e.target.value) } })} />
      </label>
      {save.isError && <p role="alert">{errorText(save.error)}</p>}
      <Button type="submit" variant="primary" disabled={!name.trim() || save.isPending}>
        Save theme
      </Button>{' '}
      <Button onClick={onDone}>Cancel</Button>
    </form>
  );
}

function A11yLevels({ environmentId }: { environmentId: string }) {
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/brand' });
  const queryClient = useQueryClient();
  const canManage = usePermissions().has('env.manage');
  const settings = useQuery({ queryKey: ['a11y-settings', environmentId], queryFn: () => api.a11ySettings(environmentId) });
  const save = useMutation({
    mutationFn: (next: A11ySettings) => api.setA11ySettings(environmentId, next),
    onSuccess: (next) => {
      queryClient.setQueryData(['a11y-settings', environmentId], next);
      // New levels change every open check.
      return queryClient.invalidateQueries({ queryKey: ['a11y', environmentId] });
    },
  });
  if (!settings.data) return null;
  const s = settings.data;
  return (
    <section aria-label="Accessibility rules" className="panel">
      <h2>Accessibility rules</h2>
      <p className="hint">A warning shows in the editor; an error also blocks publishing.</p>
      <table className="table" aria-label="Rule levels">
        <tbody>
          {CODES.map(([code, label]) => (
            <tr key={code}>
              <th scope="row">{label}</th>
              <td>
                <select aria-label={`Level of ${label}`} value={s.severity[code] ?? 'warning'} disabled={!canManage || save.isPending} onChange={(e) => save.mutate({ ...s, severity: { ...s.severity, [code]: e.target.value as 'warning' | 'error' } })}>
                  <option value="warning">Warning</option>
                  <option value="error">Error (blocks publishing)</option>
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <label>
        <input type="checkbox" checked={s.localizationEnabled} disabled={!canManage || save.isPending} onChange={(e) => save.mutate({ ...s, localizationEnabled: e.target.checked })} /> Messages are localized (each needs an accessibility language)
      </label>
      {save.isError && <p role="alert">{errorText(save.error)}</p>}
    </section>
  );
}

/**
 * Settings → Brand (BB-60): brand themes (colours, fonts, radii; one default) with a live accessibility check of a
 * sample message in the theme, and the environment's accessibility rule levels.
 */
export function BrandThemesPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/settings/brand' });
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/brand' });
  const queryClient = useQueryClient();
  const canEdit = usePermissions().has('content.edit');
  const [editing, setEditing] = useState<BrandThemeView | 'new' | null>(null);
  const themes = useQuery({ queryKey: ['brand-themes', environmentId], queryFn: () => api.listBrandThemes(environmentId) });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['brand-themes', environmentId] });
  const makeDefault = useMutation({ mutationFn: (id: string) => api.makeDefaultBrandTheme(environmentId, id), onSuccess: refresh });
  const archive = useMutation({ mutationFn: (id: string) => api.archiveBrandTheme(environmentId, id), onSuccess: refresh });
  return (
    <section>
      <h1>Brand</h1>
      {canEdit && !editing && <Button onClick={() => setEditing('new')}>New theme</Button>}
      {editing && <ThemeForm key={editing === 'new' ? 'new' : editing.id} environmentId={environmentId} theme={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />}
      {themes.data?.items.length === 0 && <p className="hint">No themes yet. The first theme becomes the default.</p>}
      <ul aria-label="Themes" className="plain-list">
        {themes.data?.items.map((t) => (
          <li key={t.id} className="panel">
            <article aria-label={`Theme ${t.name}`}>
              <h2>
                {t.name} {t.isDefault && <StatusPill tone="success">Default</StatusPill>}
              </h2>
              <p className="hint">
                {COLORS.map(([key, label]) => `${label} ${t.tokens.colors[key]}`).join(' · ')} · {t.tokens.fonts.family}
              </p>
              {canEdit && (
                <div className="toolbar">
                  <Button onClick={() => setEditing(t)}>Edit</Button>
                  {!t.isDefault && <Button onClick={() => makeDefault.mutate(t.id)}>Make default</Button>}
                  {!t.isDefault && (
                    <Button variant="danger" onClick={() => archive.mutate(t.id)}>
                      Archive
                    </Button>
                  )}
                </div>
              )}
              <A11yIssuesPanel environmentId={environmentId} channel="in_app" content={SAMPLE} themeId={t.id} />
            </article>
          </li>
        ))}
      </ul>
      {(makeDefault.isError || archive.isError) && <p role="alert">{errorText(makeDefault.error ?? archive.error)}</p>}
      <A11yLevels environmentId={environmentId} />
    </section>
  );
}
